import useTranslator from '@hooks/use-translator';
import { Input } from '@components/common/input';
import { Button } from '@components/common/button';
import {
  Check,
  CheckCheck,
  PlusCircle,
  Save,
  SaveAll,
  SaveIcon,
  X,
} from 'lucide-react';
import axios from 'axios';
import {
  PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from '@pma/utils/api-routes';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@stores/auth-store';
import { parseCookies } from 'nookies';
import { ACCESS_TOKEN } from '@pma/utils/constants';
import { useState } from 'react';
import { DictType } from '@pma/types';
import { toast } from '@hooks/use-toast';
import { cn } from '@pma/utils/helpers';
import { BilingualInput } from '@components/common/bilingual-input';

interface Props {
  refetch?: CallableFunction;
  option?: DictType;
}
const AddWorkTypeForm = ({ refetch, option }: Props) => {
  const hasOptions = !!option?.id;
  const workTypeId = option?.id;
  const [workType, setWorkType] = useState<DictType>(
    hasOptions
      ? option?.value!
      : {
          he: '',
          en: '',
        },
  );
  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();

  const isHebrew = locale === 'he';

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const orgId = authConfig?.last_organization_id;

  const addWorkType = async () => {
    const response = await axios.post(
      SERVER_URL + PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT + '/add',
      {
        organization_id: orgId,
        hebrew: workType?.he,
        english: workType?.en,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const editWorkType = async () => {
    const response = await axios.post(
      SERVER_URL + PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT + '/edit',
      {
        work_type_id: workTypeId,
        hebrew: workType?.he,
        english: workType?.en,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };
  const addWorkTypeMutation = useMutation({
    mutationKey: ['add-edit', 'work-type', workTypeId],
    mutationFn: workTypeId ? editWorkType : addWorkType,
    onSuccess: (data) => {
      setWorkType({
        he: '',
        en: '',
      });
      refetch && refetch();
      hasOptions && option?.onCancel();
    },
    onError: (error: DictType) => {
      toast({
        title: translate('Error'),
        description: error?.response?.data.message ||
          `${translate('Error occurred while updating')} ${translate('type')}`,
        variant: 'error',
      });
    },
  });

  const onSubmit = () => {
    addWorkTypeMutation.mutate();
  };

  return (
    <div
      className={cn('flex items-center gap-2 mt-2', isHebrew ? 'rtl' : 'ltr')}
    >
      <BilingualInput
        className='rounded'
        value={{ he: workType?.he, en: workType?.en }}
        type='input'
        textareaRows={2}
        placeholder={{
          en: translate('Type'),
          he: translate('Type'),
        }}
        onChange={(value: any) => {
          setWorkType((prev) => ({
            ...prev,
            en: value?.en,
            he: value.he,
          }));
        }}
        required
      />
      {hasOptions ? (
        <div className='flex flex-col gap-0.5'>
          <Button
            onClick={option.onCancel}
            type='button'
            size={'icon'}
            variant={'outline'}
            className='rounded-full h-4 w-4 m-0 p-0'
          >
            <X className=' h-4 w-4 rounded-full' />
          </Button>
          <Button
            onClick={onSubmit}
            type='button'
            size={'icon'}
            variant={'secondary'}
            className='rounded-full text-black h-4 w-4 m-0 p-0.5'
          >
            <Check className='w-3 h-3' />
          </Button>
        </div>
      ) : (
        <Button
          onClick={onSubmit}
          type='button'
          size={'sm'}
          className='rounded-full h-7 w-7 m-0 p-0'
        >
          <PlusCircle className=' h-7 w-7 rounded-full' />
        </Button>
      )}
    </div>
  );
};

export default AddWorkTypeForm;
